/*  Greatly Social – StockPickerModal  (v3.0 – modernized UI)
    All logic identical to v2.
------------------------------------------------------------------------ */
import React, { useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { PlayFill } from 'react-bootstrap-icons'

export default function StockPickerModal ({ show, onClose, onFilesSelect, userId }) {
  const [query,     setQuery]    = useState('')
  const [mediaType, setMedia]    = useState('image')
  const [results,   setResults]  = useState([])
  const [loading,   setLoading]  = useState(false)
  const [selected,  setSelected] = useState({})

  const search = async () => {
    if (!query.trim()) return
    setLoading(true); setResults([])
    try {
      const res = await fetch(
        `/api/stock/search?query=${encodeURIComponent(query)}&type=${mediaType}`,
        { credentials: 'include' }
      )
      const { items } = await res.json()
      setResults(items)
    } catch (err) {
      alert('Stock search error: ' + err.message)
    } finally {
      setLoading(false)
    }
  }

  const toggleItem = item =>
    setSelected(prev =>
      prev[item.url]
        ? (() => { const { [item.url]: _o, ...rest } = prev; return rest })()
        : { ...prev, [item.url]: item }
    )

  const handleAdd = async () => {
    if (!Object.keys(selected).length) return
    const imported = await Promise.all(
      Object.values(selected).map(async i => {
        let finalUrl = i.url
        try {
          const rsp = await fetch('/api/stock/import', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ userId, sourceUrl: i.url })
          })
          const data = await rsp.json()
          if (data.url) finalUrl = data.url
        } catch (err) {
          console.error('Stock → S3 import failed:', err)
        }
        return { key: `stock-${i.id}`, url: finalUrl, file: null }
      })
    )
    onFilesSelect(imported)
    setSelected({})
  }

  const selectedCount = Object.keys(selected).length

  if (!show) return null

  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:1050, backdropFilter:'blur(2px)' }} />

      {/* Panel */}
      <div style={{
        position    : 'fixed',
        top         : '50%',
        left        : '50%',
        transform   : 'translate(-50%, -50%)',
        zIndex      : 1051,
        background  : '#ffffff',
        borderRadius: 16,
        width       : 740,
        maxWidth    : 'calc(100vw - 32px)',
        maxHeight   : '90vh',
        display     : 'flex',
        flexDirection:'column',
        boxShadow   : '0 20px 60px rgba(0,0,0,0.18)',
        overflow    : 'hidden',
      }}>
        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'18px 22px 14px', borderBottom:'1px solid #f3f4f6' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ width:32, height:32, borderRadius:'50%', background:'#eff6ff', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <i className='fas fa-photo-video' style={{ fontSize:13, color:'#2563eb' }} />
            </div>
            <h3 style={{ fontSize:16, fontWeight:800, color:'#111827', margin:0 }}>Import Stock Content</h3>
          </div>
          <button onClick={onClose} style={{ background:'transparent', border:'none', cursor:'pointer', color:'#9ca3af', fontSize:20, lineHeight:1 }}>×</button>
        </div>

        {/* Search bar */}
        <div style={{ padding:'16px 22px', borderBottom:'1px solid #f3f4f6', display:'flex', gap:8, flexWrap:'wrap' }}>
          <input
            type='text'
            placeholder='Search stock media…'
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); search() } e.stopPropagation() }}
            style={{
              flex:1, minWidth:200, padding:'9px 12px', borderRadius:8,
              border:'1.5px solid #e5e7eb', fontSize:13, color:'#111827',
              outline:'none', boxSizing:'border-box',
            }}
            onFocus={e => e.target.style.borderColor = '#2563eb'}
            onBlur={e => e.target.style.borderColor = '#e5e7eb'}
          />
          {/* Media type toggle */}
          <div style={{ display:'flex', background:'#f3f4f6', borderRadius:8, padding:3 }}>
            {['image','video'].map(t => (
              <button
                key={t}
                type='button'
                onClick={() => setMedia(t)}
                style={{
                  padding:'6px 14px', borderRadius:6, border:'none',
                  background: mediaType===t ? '#ffffff' : 'transparent',
                  color: mediaType===t ? '#111827' : '#6b7280',
                  fontWeight: mediaType===t ? 700 : 500,
                  fontSize:13, cursor:'pointer',
                  boxShadow: mediaType===t ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  transition:'all 0.12s', textTransform:'capitalize',
                }}
              >
                {t === 'image' ? '🖼 Images' : '🎬 Videos'}
              </button>
            ))}
          </div>
          <button
            type='button'
            onClick={search}
            style={{
              padding:'9px 18px', borderRadius:8, border:'none',
              background:'#2563eb', color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer',
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#1d4ed8'}
            onMouseLeave={e => e.currentTarget.style.background = '#2563eb'}
          >
            Search
          </button>
        </div>

        {/* Results */}
        <div style={{ flex:1, overflowY:'auto', padding:'16px 22px' }}>
          {loading && (
            <div style={{ display:'flex', justifyContent:'center', padding:'40px 0' }}>
              <Spinner animation='border' style={{ color:'#2563eb' }} />
            </div>
          )}

          {!loading && results.length > 0 && (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(120px, 1fr))', gap:10 }}>
              {results.map(item => {
                const isSelected = !!selected[item.url]
                return (
                  <div
                    key={item.id}
                    onClick={() => toggleItem(item)}
                    style={{
                      position    : 'relative',
                      borderRadius: 10,
                      overflow    : 'hidden',
                      cursor      : 'pointer',
                      border      : `2px solid ${isSelected ? '#2563eb' : 'transparent'}`,
                      boxShadow   : isSelected ? '0 0 0 3px rgba(37,99,235,0.2)' : '0 1px 3px rgba(0,0,0,0.1)',
                      transition  : 'all 0.15s',
                      aspectRatio : '1',
                    }}
                  >
                    <img src={item.thumb} alt='' style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} />
                    {item.type === 'video' && (
                      <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', background:'rgba(0,0,0,0.2)' }}>
                        <PlayFill size={24} color='#fff' />
                      </div>
                    )}
                    {isSelected && (
                      <div style={{ position:'absolute', inset:0, background:'rgba(37,99,235,0.35)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                        <div style={{ width:28, height:28, borderRadius:'50%', background:'#2563eb', display:'flex', alignItems:'center', justifyContent:'center', border:'2px solid #fff' }}>
                          <i className='fas fa-check' style={{ fontSize:11, color:'#fff' }} />
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}

          {!loading && !results.length && query && (
            <div style={{ textAlign:'center', padding:'40px 0', color:'#9ca3af', fontSize:14 }}>
              <i className='fas fa-search' style={{ fontSize:28, marginBottom:10, display:'block', opacity:0.4 }} />
              No {mediaType}s found for "{query}"
            </div>
          )}

          {!loading && !results.length && !query && (
            <div style={{ textAlign:'center', padding:'40px 0', color:'#9ca3af', fontSize:14 }}>
              <i className='fas fa-photo-video' style={{ fontSize:28, marginBottom:10, display:'block', opacity:0.4 }} />
              Search for images or videos to get started
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding:'14px 22px', borderTop:'1px solid #f3f4f6', display:'flex', justifyContent:'space-between', alignItems:'center', gap:10 }}>
          <span style={{ fontSize:13, color:'#6b7280' }}>
            {selectedCount > 0 ? `${selectedCount} item${selectedCount !== 1 ? 's' : ''} selected` : 'Click items to select'}
          </span>
          <div style={{ display:'flex', gap:8 }}>
            <button type='button' onClick={onClose} style={{ padding:'8px 16px', borderRadius:8, border:'1.5px solid #e5e7eb', background:'transparent', color:'#374151', fontSize:13, fontWeight:600, cursor:'pointer' }}>
              Cancel
            </button>
            <button
              type='button'
              onClick={handleAdd}
              disabled={!selectedCount}
              style={{
                padding:'8px 18px', borderRadius:8, border:'none',
                background: selectedCount ? '#2563eb' : '#e5e7eb',
                color: selectedCount ? '#fff' : '#9ca3af',
                fontSize:13, fontWeight:700, cursor: selectedCount ? 'pointer' : 'not-allowed',
                transition:'background 0.15s',
              }}
            >
              Add {selectedCount > 0 ? `${selectedCount} Item${selectedCount !== 1 ? 's' : ''}` : 'Selected'}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}